import React, { useState, useRef, useCallback, useEffect } from 'react';
import AvatarLightbox from './AvatarLightbox';
import './ConversationItem.css';

const HOVER_DELAY_MS = 3000; // 3 segundos para revelar botão arquivar

function ConversationItem({
  conversation,
  isSelected,
  onClick,
  onDoubleClick,
  onArchive,           // (conversation) => void
  onClickArchived,     // (conversation) => void  — chamado ao clicar em conversa arquivada
  selectionMode = false,           // true quando lista está em modo seleção múltipla
  isChecked = false,               // true se essa conversa está marcada
  onToggleCheck,                   // (conversation) => void  — chamado ao marcar/desmarcar
}) {
  const [showArchiveBtn, setShowArchiveBtn] = useState(false);
  const hoverTimerRef = useRef(null);

  // Limpa timer ao desmontar
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const startHoverTimer = useCallback(() => {
    // Desabilitado durante modo seleção — evita confusão de UX
    if (selectionMode) return;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      setShowArchiveBtn(true);
    }, HOVER_DELAY_MS);
  }, [selectionMode]);

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShowArchiveBtn(false);
  }, []);

  if (!conversation || !conversation.contact) return null;

  const isArchived = conversation.status === 'archived';

  const handleClick = () => {
    // Modo seleção: click marca/desmarca, não abre o chat
    if (selectionMode) {
      if (onToggleCheck && typeof onToggleCheck === 'function') {
        onToggleCheck(conversation);
      }
      return;
    }

    // Se a conversa está arquivada (apareceu na busca),
    // delegar pro handler especial — desarquiva e abre.
    if (isArchived && onClickArchived && typeof onClickArchived === 'function') {
      onClickArchived(conversation);
      return;
    }
    if (onClick && typeof onClick === 'function') {
      onClick(conversation);
    }
  };

  const handleDoubleClick = () => {
    if (onDoubleClick && typeof onDoubleClick === 'function') {
      onDoubleClick(conversation);
    }
  };

  const handleArchiveClick = (e) => {
    e.stopPropagation(); // não disparar clique do item
    if (onArchive && typeof onArchive === 'function') {
      onArchive(conversation);
    }
    setShowArchiveBtn(false);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const displayName = conversation.contact.name ||
    conversation.contact.phone ||
    'Sem nome';

  const avatarLetter = displayName?.[0]?.toUpperCase() || '?';

  const getLastMessagePreview = () => {
    // Se veio de busca por conteúdo de mensagem, mostrar o snippet
    if (conversation.match_snippet) {
      const snippet = conversation.match_snippet;
      return snippet.length > 40 ? snippet.substring(0, 40) + '...' : snippet;
    }
    const content = conversation.last_message_content;
    if (!content) return 'Nenhuma mensagem';
    if (content.startsWith('🎤')) return '🎤 Áudio';
    if (content.startsWith('📷')) return '📷 Foto';
    if (content.startsWith('📄')) return '📄 Documento';
    if (content.startsWith('🎥')) return '🎥 Vídeo';
    return content.length > 40 ? content.substring(0, 40) + '...' : content;
  };

  const getMessageStatus = () => {
    if (conversation.last_message_direction === 'outbound') {
      if (conversation.last_message_status === 'read') return '✓✓';
      if (conversation.last_message_status === 'delivered') return '✓✓';
      if (conversation.last_message_status === 'sent') return '✓';
      return '🕐';
    }
    return null;
  };

  // Classes condicionais
  const itemClass = [
    'conversation-item',
    isSelected ? 'selected' : '',
    isArchived ? 'archived' : '',
    selectionMode ? 'selection-mode' : '',
    selectionMode && isChecked ? 'checked' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={itemClass}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={startHoverTimer}
      onMouseLeave={cancelHoverTimer}
    >
      {/* Checkbox de seleção (só aparece em modo seleção) */}
      {selectionMode && (
        <div className={`selection-checkbox ${isChecked ? 'checked' : ''}`} aria-hidden="true">
          {isChecked && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}

      <AvatarLightbox
        src={conversation.contact.avatar_url}
        alt={displayName}
        className="avatar"
        fallback={
          <div className="avatar-placeholder">{avatarLetter}</div>
        }
      />

      <div className="conversation-info">
        <div className="conversation-header">
          <h4>
            {displayName}
            {isArchived && (
              <span className="archived-badge" title="Conversa arquivada — clique para reabrir">
                Arquivada
              </span>
            )}
          </h4>
          <span className="time">{formatTime(conversation.last_message_at)}</span>
        </div>

        <div className="conversation-preview">
          <div className="last-message">
            {getMessageStatus() && (
              <span className={`message-status ${conversation.last_message_status === 'read' ? 'read' : ''}`}>
                {getMessageStatus()}{' '}
              </span>
            )}
            <span className="preview-text">{getLastMessagePreview()}</span>
          </div>

          {conversation.unread_count > 0 && (
            <span className="unread-badge">{conversation.unread_count}</span>
          )}
        </div>
      </div>

      {/* Botão fechar — só aparece após hover de 3s, e só em conversas ativas, e NÃO em modo seleção */}
      {showArchiveBtn && !isArchived && !selectionMode && (
        <button
          type="button"
          className="archive-btn"
          onClick={handleArchiveClick}
          title="Fechar conversa"
          aria-label="Fechar conversa"
        >
          Fechar
        </button>
      )}
    </div>
  );
}

export default ConversationItem;
